import { useEffect } from "react"
import { useQueryState } from "nuqs"
import usePagination from "../../hooks/usePagination"
import {
  useCustomerSuccessCenters,
  useCustomerSuccessOverviewCounts,
} from "../../hooks/useCustomerSuccessDeals"
import OverviewStats from "./components/OverviewStats"
import CenterToolbar, { ALL } from "./components/CenterToolbar"
import CentersList from "./components/CentersList"

const CustomerSuccess = () => {
  const { limit, onPaginationChange, page, pagination } = usePagination()

  const [centerName, setCenterName] = useQueryState("center", {
    defaultValue: "",
  })
  const [status, setStatus] = useQueryState("status", { defaultValue: ALL })

  const filters = {
    centerName: centerName || undefined,
    status: status === ALL ? undefined : status,
  }

  // Real (non-infinite) pagination — a filter change must snap back to page
  // 1, otherwise sitting on page 3 and applying a filter can silently render
  // an empty list with no explanation.
  useEffect(() => {
    onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerName, status])

  const {
    data: { data = [], total = 0 } = {},
    isLoading,
    isFetching,
    isError,
    error,
  } = useCustomerSuccessCenters({ page, limit, filters })

  const pageCount = total ? Math.ceil(total / limit) : 0

  const overview = useCustomerSuccessOverviewCounts({})

  // Global per-status center counts for the filter tabs — not narrowed by
  // the center-name search.
  const statusCounts = {
    [ALL]: overview.centers.total,
    IN_PROGRESS: overview.centers.inProgress,
    CEO_DUE: overview.centers.ceoOutreachDueCenters,
    COMPLETE: overview.centers.fullyConfirmed,
  }

  const hasFilters = Boolean(centerName) || status !== ALL
  const clearFilters = () => {
    setCenterName("")
    setStatus(ALL)
  }

  return (
    <div className="p-4">
      <h1 className="mb-4 text-2xl font-extrabold text-primary">
        Customer Success
      </h1>

      <OverviewStats
        centers={overview.centers}
        csatCounts={overview.csatCounts}
        grandTotal={overview.grandTotal}
        trackingCounts={overview.trackingCounts}
      />

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <CenterToolbar
          centerName={centerName}
          counts={statusCounts}
          isFetching={isFetching}
          setCenterName={setCenterName}
          setStatus={setStatus}
          status={status}
          studyLimit={overview.centers.studyLimit}
        />

        <CentersList
          data={data}
          error={error}
          hasFilters={hasFilters}
          isError={isError}
          isLoading={isLoading}
          pageCount={pageCount}
          pagination={pagination}
          studyLimit={overview.centers.studyLimit}
          total={total}
          onClearFilters={clearFilters}
          onPaginationChange={onPaginationChange}
        />
      </div>
    </div>
  )
}

export default CustomerSuccess
